'use client';

/**
 * HubSpot Meetings embed — pure iframe load of the meeting page. The
 * widget grows internally with no postMessage we can hook, so we use a
 * generous {@code min-height: 700px}; small calendars get extra
 * whitespace, large ones don't clip.
 *
 * <p>Authors supply the meeting PATH only (e.g. {@code 'jane/30min'});
 * URL is built from a vetted base.
 */
import { useTranslation } from 'react-i18next';
import { EmbedCard, ConfigurationError, CalendarIcon } from './_shared';

interface HubSpotMeetingsBlockProps {
  title?: string;
  /** Path after {@code meetings.hubspot.com/} — 2-3 alphanumeric/
   *  dot/dash/underscore segments. */
  meetingPath: string;
}

const HUBSPOT_PATH_RE = /^[a-z0-9._-]+(?:\/[a-z0-9._-]+){1,2}$/i;

export function HubSpotMeetingsBlock({ meetingPath, title }: HubSpotMeetingsBlockProps) {
  const { t } = useTranslation('site-builder-blocks');
  if (!meetingPath || !HUBSPOT_PATH_RE.test(meetingPath)) {
    return <ConfigurationError message={t('embeds.hubspotMeetings.invalidPath')} />;
  }
  const src = `https://meetings.hubspot.com/${encodeURI(meetingPath)}?embed=true`;
  return (
    <EmbedCard title={title} accent={<CalendarIcon />}>
      <iframe
        src={src}
        title={title ?? t('embeds.hubspotMeetings.frameTitle')}
        loading="lazy"
        style={{
          width: '100%',
          minHeight: 700,
          border: 'none',
          background: 'transparent',
        }}
      />
    </EmbedCard>
  );
}

export type { HubSpotMeetingsBlockProps };
